import type { Leaderboard } from '@brush/api-client';
import { ErrorRetry, Pill, Spinner } from '@brush/ui';
import { ArrowLeft } from 'lucide-react';
import { useLeaderboardStandings } from '../../queries';
import { ExportControl } from '../ExportControl';

const rankStyle = (rank: number) =>
  rank === 1
    ? 'bg-gold-500/20 text-ink-900'
    : rank === 2
      ? 'bg-ink-800/10 text-ink-900'
      : rank === 3
        ? 'bg-chip-500/15 text-ink-900'
        : 'bg-ink-800/5 text-ink-800/70';

/**
 * A leaderboard's published standings (§7.5) — **the latest snapshot**, not a live computation.
 *
 * That distinction is the screen's whole subject and is stated on it. Figures here move when
 * somebody recalculates and at no other time, which is what makes them safe to pay against; a
 * board showing Tuesday's snapshot with no "as of" line reads exactly like one showing today's.
 *
 * The empty state is three different sentences because the causes send an operator to three
 * different places: a board that never started has no points system pinned, a running one nobody
 * has recalculated has nothing published, and a board with a snapshot and no rows really did score
 * nobody. An undifferentiated "no standings yet" is the state that generates support calls.
 */
export function LeaderboardDetail({
  leaderboard,
  onBack,
}: {
  leaderboard: Leaderboard;
  onBack: () => void;
}) {
  const standingsQ = useLeaderboardStandings(leaderboard.id);
  const standings = standingsQ.data?.data ?? [];
  const published = standingsQ.data?.sequenceNumber ?? null;

  return (
    <div className="p-8">
      <button
        type="button"
        onClick={onBack}
        className="mb-6 flex items-center gap-2 text-sm text-ink-800/60 hover:text-ink-950"
      >
        <ArrowLeft className="size-4" /> Back to leaderboards
      </button>

      <div className="flex flex-wrap items-baseline gap-3">
        <h1 className="font-display text-2xl font-semibold text-ink-950">{leaderboard.name}</h1>
        <Pill
          tone={
            leaderboard.status === 'running'
              ? 'good'
              : leaderboard.status === 'closed'
                ? 'neutral'
                : 'warn'
          }
        >
          {leaderboard.status === 'running'
            ? 'Running'
            : leaderboard.status === 'closed'
              ? 'Closed'
              : 'Not started'}
        </Pill>
        {/* L (docs/specs/export.md EX5): the latest published snapshot, every row. In the heading row rather than the
            standings header, whose `overflow-hidden` would clip the panel. */}
        <div className="ml-auto self-center">
          <ExportControl
            availability={{ view: 'L', status: leaderboard.status }}
            target={{ view: 'L', leaderboardId: leaderboard.id }}
            viewLabel={leaderboard.name}
            subject={leaderboard.name}
          />
        </div>
      </div>
      <p className="mb-6 mt-1 text-sm text-ink-800/60">
        {leaderboard.dateStart} — {leaderboard.dateEnd}
        {leaderboard.pointsSystemVersionNumber
          ? ` · points system version ${leaderboard.pointsSystemVersionNumber}`
          : ''}
      </p>

      <section className="overflow-hidden rounded-xl border border-ink-800/10 bg-white shadow-sm">
        <header className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 border-b border-ink-800/10 px-6 py-4">
          <div>
            <h2 className="font-display font-semibold text-ink-950">Standings</h2>
            <p className="mt-0.5 text-xs text-ink-800/60">
              {published === null
                ? 'Nothing published yet.'
                : /* Never "live". These are frozen figures, and saying when they were frozen is
                     what stops a stale board being read as a current one. */
                  `Snapshot ${published}, taken ${formatWhen(standingsQ.data?.computedAt ?? null)}. They change only when the leaderboard is recalculated.`}
            </p>
          </div>
          {standingsQ.data?.isFinal ? <Pill tone="strong">Final</Pill> : null}
        </header>

        {standingsQ.isLoading ? (
          <Spinner label="Loading standings…" />
        ) : standingsQ.isError ? (
          <div className="p-6">
            <ErrorRetry message="Couldn't load standings." onRetry={() => void standingsQ.refetch()} />
          </div>
        ) : standings.length === 0 ? (
          <p className="p-8 text-center text-sm leading-relaxed text-ink-800/55">
            {leaderboard.status === 'draft' ? (
              <>
                This leaderboard hasn’t started.
                <br />
                <span className="text-xs">
                  Edit it, pick a points system and press <strong>Start leaderboard</strong> — that
                  pins the version it will score with.
                </span>
              </>
            ) : published === null ? (
              <>
                No standings have been published yet.
                <br />
                <span className="text-xs">
                  Edit this leaderboard and press <strong>Recalculate now</strong> to publish them.
                </span>
              </>
            ) : (
              <>
                No player scored in the events this leaderboard covers.
                <br />
                <span className="text-xs">
                  Check which events are in scope, and that their results have been entered.
                </span>
              </>
            )}
          </p>
        ) : (
          <>
            {/*
             * Two arrangements, the same rule the tournament sheet follows — and the same
             * defect underneath: five columns at `px-6` spend 240px on padding alone against the
             * 366px `p-8` leaves at the 430px reference width, and this section is
             * `overflow-hidden` with no scroller, so Points — the column the board exists for
             * — fell off the right-hand edge with no gesture that could reach it.
             *
             * Below `md` the row folds to two lines. Rank, player and points hold the first line;
             * the card number and the two counts demote to the second. The avatar goes: it is
             * 44px of the width with the card number saying the same thing one line down.
             */}
            <ul className="divide-y divide-ink-800/5 md:hidden print:hidden">
              {standings.map((s) => (
                <li key={s.player.id} className="flex items-start gap-3 px-4 py-3">
                  <RankBadge rank={s.rank} isTied={s.isTied} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium text-ink-950">{s.player.name}</span>
                    {/* "all time" survives the fold. It is the qualifier the column heading
                        carries for a reason — a snapshot holds standings, not a results sheet,
                        so this is not the board's own cash count and must not read as one. */}
                    <span className="num mt-0.5 block text-xs text-ink-800/60">
                      {s.player.cardId} · {s.eventsScored} event{s.eventsScored === 1 ? '' : 's'} ·{' '}
                      {s.player.cashes} cashes (all time)
                    </span>
                  </span>
                  <span className="num shrink-0 text-sm font-semibold text-ink-950">
                    {trimPoints(s.points)}
                  </span>
                </li>
              ))}
            </ul>

            <table className="hidden w-full text-left text-sm md:table print:table">
              <thead className="bg-felt-50/50 text-xs uppercase tracking-wide text-ink-800/60">
                <tr>
                  <th className="px-6 py-3 font-medium">Rank</th>
                  <th className="px-6 py-3 font-medium">Player</th>
                  <th className="px-6 py-3 text-right font-medium">Events scored</th>
                  {/* All-time figures on the player, labelled as such. The snapshot holds standings
                      rather than a results sheet, and recomputing this board's own cashes on a read
                      is the live computation §8 exists to avoid. */}
                  <th className="px-6 py-3 text-right font-medium">Cashes (all time)</th>
                  <th className="px-6 py-3 text-right font-medium">Points</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-800/5">
                {standings.map((s) => (
                  <tr key={s.player.id} className="hover:bg-felt-50/30">
                    <td className="px-6 py-3">
                      <RankBadge rank={s.rank} isTied={s.isTied} />
                    </td>
                    <td className="px-6 py-3">
                      <div className="flex items-center gap-3">
                        <span className="flex size-8 items-center justify-center rounded-full bg-ink-950 text-xs font-semibold text-white">
                          {s.player.avatar}
                        </span>
                        <div>
                          <div className="font-medium text-ink-950">{s.player.name}</div>
                          <div className="text-xs text-ink-800/60">{s.player.cardId}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-3 text-right text-ink-950">{s.eventsScored}</td>
                    <td className="px-6 py-3 text-right text-ink-800/70">{s.player.cashes}</td>
                    <td className="px-6 py-3 text-right font-semibold text-ink-950">
                      {trimPoints(s.points)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </section>
    </div>
  );
}

/**
 * The snapshot's rank, never the row index: tied players share a rank and the next one skips
 * (1, 2, 2, 4). One component because both arrangements draw it, and a tie marker present in one
 * and missing from the other would be invisible until somebody compared the two widths.
 */
function RankBadge({ rank, isTied }: { rank: number; isTied: boolean }) {
  return (
    <span className="inline-flex shrink-0 items-center">
      <span
        className={`inline-flex size-7 items-center justify-center rounded-full text-xs font-semibold ${rankStyle(rank)}`}
      >
        {rank}
      </span>
      {isTied ? <span className="ml-1 text-[10px] text-ink-800/45">T</span> : null}
    </span>
  );
}

/**
 * Points are stored to four decimal places, and almost every system produces round numbers — so
 * `90.0000` is noise on every row. Trailing zeroes go; genuine precision stays.
 */
function trimPoints(points: number): string {
  return String(Math.round(points * 10000) / 10000);
}

/** An ISO timestamp as something an operator reads. Falls back to the raw value rather than NaN. */
function formatWhen(iso: string | null): string {
  if (!iso) return 'at an unknown time';
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return iso;
  return parsed.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}
